import React, { useEffect, useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import { Card, Spinner, Button } from "reactstrap";
import DropdownFilter from "../../../../utils/DropdownFilter" 
import helpers, { LOGS_PAGE_SIZE_OPTIONS } from "../../../../utils/helpers";
import Pagination from "views/pages/components/Pagination";
import userServices from "../../../../services/userServices";
import { useTranslation } from "../../../../context/TranslationProvider";


const formatEventLabel = (eventType) => {
  if (!eventType) return "-";
  return eventType
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const formatSource = (source) => {
  if (!source) return "-";
  return source.charAt(0).toUpperCase() + source.slice(1);
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleString(); // you can change to your format
};


function UserActivityLogsTable({ userId }) {
  const { t, language } = useTranslation();
  const EVENT_FILTER_OPTIONS = useMemo(
    () => [
      { label: t("common.all", "All"), value: helpers.EVENT_FILTERS.ALL },
      ...Object.values(helpers.EVENT_FILTERS)
        .filter((e) => e !== helpers.EVENT_FILTERS.ALL)
        .map((event) => ({
          label: t(`activityEvents.${event}`),
          value: event,
        })),
    ],
    [t]
  );

  const [isLoading, setIsLoading] = useState(true);
  const [logs, setLogs] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [total, setTotal] = useState(0);

  const [filterEvent, setFilterEvent] = useState(helpers.EVENT_FILTERS.ALL);
  const handleClearFilters = () => {
    setFilterEvent(helpers.EVENT_FILTERS.ALL);
    setPage(1);
  };

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const res = await userServices.getActivityLogs({
        userId,
        eventType: filterEvent,
        page,
        limit,
      });

      if (res?.success) {
        // Use only userLogs here
        setLogs(res.userLogs || []);
        setTotal(res.meta?.user?.totalItems || 0);
      } else {
        setLogs([]);
        setTotal(0);
      }
    } catch (e) {
      setLogs([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset to page 1 when filter changes
  useEffect(() => {
    setPage(1);
  }, [filterEvent, userId]);

  useEffect(() => {
    if (!userId) return;
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, page, filterEvent, limit]);

  const columns = useMemo(
    () => [
      {
        dataField: "eventType",
        text: t("table.headers.event", "Event"),
        headerAlign: "left",
        align: "left",
        formatter: (_, row) => formatEventLabel(row.eventType),
      },
      {
        dataField: "source",
        text: t("table.headers.source", "Source"),
        headerAlign: "left",
        align: "left",
        formatter: (_, row) => formatSource(row.source),
      },
      {
        dataField: "ipAddress",
        text: t("table.headers.ip", "IP Address"),
        headerAlign: "left",
        align: "left",
        formatter: (_, row) => row.ipAddress || "-",
      },
      {
        dataField: "createdAt",
        text: t("table.headers.createdAt", "Created At"),
        headerAlign: "left",
        align: "left",
        sort: true,
        formatter: (_, row) => formatDateTime(row.createdAt),
      },
    ],
    [t]
  );

  const renderLoading = () => (
    <div className="center-loader">
      <div className="center-loader-content">
        <Spinner/>
        <p className="mt-3 text-muted">
          {t("common.loading", "Loading....")}
        </p>
      </div>
    </div>
  );

  return (
    <Card className="shadow mb-4">
      <div className="py-4">
        <div className="listing-header-row px-4 pb-1 mb-3">
          <div className="listing-header-left">
            <h4 className="mb-0 text-primary font-weight-bold">
              {t("table.headers.userActivityLogs", "User Activity Logs")}
            </h4>
          </div>
          <div className="listing-header-right d-flex align-items-center">
            <DropdownFilter
              label={t("table.filterBy", "Filter By")}
              value={filterEvent}
              options={EVENT_FILTER_OPTIONS}
              onChange={(value) => setFilterEvent(value)}
              buttonClass="btn-neutral filter-select2"
              capitalize
            />
            <Button
              size="sm"
              className="ml-2 btn-neutral text-danger font-weight-600"
              onClick={handleClearFilters}
            >
              {t("common.clearFilters", "Clear Filters")}
            </Button>
          </div>

        </div>

        {isLoading ? (
          renderLoading()
        ) : (
          <ToolkitProvider
            data={logs}
            keyField="_id"
            columns={columns}
            search={false}
          >
            {(props) => (
              <div className="px-4">
                <div className="table-responsive">
                  <BootstrapTable
                    key={language}
                    {...props.baseProps}
                    bootstrap4
                    bordered
                    remote={{ pagination: true }}
                    onTableChange={(type, { sortField, sortOrder }) => {
                      // Handled by other logic if needed, but pagination is now manual
                    }}

                    noDataIndication={() => (
                      <div className="text-center py-6 text-muted d-flex flex-column align-items-center">
                        <span className="text-sm md:text-base">{t("page.userActivityLogs.noActivityLogs", "No activity logs found")}</span>
                      </div>
                    )}
                  />

                </div>
                <Pagination
                  totalRows={total}
                  rowsPerPage={limit}
                  currentPage={page - 1}
                  onPageChange={(p) => setPage(p + 1)}
                  onRowsPerPageChange={(size) => {
                    setLimit(size);
                    setPage(1);
                  }}
                  rowsPerPageOptions={LOGS_PAGE_SIZE_OPTIONS}
                  t={t}
                />
              </div>
            )}
          </ToolkitProvider>
        )}
      </div>
    </Card>
  );
}

export default UserActivityLogsTable;